// A module executes once, but the theme editor can mount a new drawer repeatedly.
const initializedDrawers = new WeakSet();

/*
 * Deep panels. Pages ship the drawer's shell only: the first screen and the Shop by Car panel. The brand, product,
 * other-menu and region panels were about 140 KB of every page's HTML, so snippets/autoid-header-drawer.liquid
 * renders them only for the autoid-drawer view, and the drawer carries that URL (this header section, so the panels
 * come from the real menu block settings) in data-autoid-drawer-deep. They are fetched when the shopper reaches for
 * the menu, and kept in localStorage for 12 hours per country and language, the same trade the desktop panels make
 * (assets/autoid-mega-fetch.js): a menu edit takes up to 12 hours to reach a returning visitor, and a failed fetch
 * falls back to the last good copy. Without any copy, a row that opens a deep panel goes to its own link instead.
 */
const DEEP_CACHE_PREFIX = 'autoid:drawer-panels:v1:';
const DEEP_FRESH_MS = 12 * 60 * 60 * 1000;
const deepLoads = new WeakMap();

function readDeep(key) {
  try {
    const stored = JSON.parse(localStorage.getItem(DEEP_CACHE_PREFIX + key) || 'null');
    return stored && typeof stored.html === 'string' ? stored : null;
  } catch (e) {
    return null;
  }
}

function writeDeep(key, html) {
  try {
    localStorage.setItem(DEEP_CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), html }));
  } catch (e) {}
}

// The response is the whole header section; the drawer sits inside the responsive loader's <template>.
function extractDeep(responseHtml) {
  const doc = new DOMParser().parseFromString(responseHtml, 'text/html');
  const template = doc.querySelector('template[data-autoid-responsive-menu-template]');
  const stack = (template ? template.content : doc).querySelector('[data-autoid-stack]');
  if (!stack) return '';
  return Array.from(stack.children)
    .filter((panel) => panel.matches('[data-panel-id]:not([data-panel-id="root"]):not([data-panel-id="car"])'))
    .map((panel) => panel.outerHTML)
    .join('');
}

function fetchDeep(url, key) {
  const stored = readDeep(key);
  if (stored && Date.now() - stored.t < DEEP_FRESH_MS) return Promise.resolve(stored.html);

  return fetch(url, { credentials: 'same-origin' })
    .then((response) => {
      if (!response.ok) throw new Error(`autoid drawer panels ${response.status}`);
      return response.text();
    })
    .then((text) => {
      const html = extractDeep(text);
      if (!html) throw new Error('autoid drawer panels: none in the response');
      writeDeep(key, html);
      return html;
    })
    .catch((error) => {
      if (stored) return stored.html;
      throw error;
    });
}

/** Adds the deep panels to the drawer's stack once. A failure is forgotten, so the next intent retries. */
function loadDeepPanels(drawer) {
  const url = drawer.dataset.autoidDrawerDeep;
  if (!url) return Promise.resolve();
  if (deepLoads.has(drawer)) return deepLoads.get(drawer);

  const load = fetchDeep(url, drawer.dataset.autoidDrawerKey || '').then((html) => {
    const stack = drawer.querySelector('[data-autoid-stack]');
    if (!stack) return;
    const template = document.createElement('template');
    template.innerHTML = html;
    template.content.querySelectorAll('[data-panel-id]').forEach((panel) => {
      if (stack.querySelector(`[data-panel-id="${CSS.escape(panel.dataset.panelId)}"]`)) panel.remove();
    });
    stack.append(template.content);
    delete drawer.dataset.autoidDrawerDeep;
  });
  deepLoads.set(drawer, load);
  load.catch(() => deepLoads.delete(drawer));
  return load;
}
function initAutoidDrawer(root = document){const drawer=root.querySelector('[data-autoid-drawer]');if(!drawer||initializedDrawers.has(drawer))return;const stackEl=drawer.querySelector('[data-autoid-stack]');const titleEl=drawer.querySelector('[data-drawer-title]');const backBtn=drawer.querySelector('[data-back-panel]');const details=drawer.closest('details');if(!stackEl||!titleEl)return;initializedDrawers.add(drawer);const panels=new Map();
// Run again once the deep panels arrive.
function registerPanels(){drawer.querySelectorAll('[data-panel-id]').forEach((p)=>{panels.set(p.getAttribute('data-panel-id'),p)})}
registerPanels();
function warmDeepPanels(){loadDeepPanels(drawer).then(registerPanels).catch(()=>{})}
let stack=['root'];const PANEL_FOCUSABLE_SELECTOR=['a[href]','button:not([disabled])','input:not([disabled]):not([type="search"])','select:not([disabled])','textarea:not([disabled])','[tabindex]:not([tabindex="-1"])'].join(',');function focusFirstIn(panel){if(!panel)return;const first=panel.querySelector(PANEL_FOCUSABLE_SELECTOR);if(!first)return;try{first.focus({preventScroll:!0})}catch(e){first.focus()}}
function applyStack(moveFocus){const topId=stack[stack.length-1];const outIds=new Set(stack.slice(0,-1));panels.forEach((panel,id)=>{panel.classList.remove('is-active','is-out-left');if(id===topId){panel.classList.add('is-active');panel.setAttribute('aria-hidden','false')}else if(outIds.has(id)){panel.classList.add('is-out-left');panel.setAttribute('aria-hidden','true')}else{panel.setAttribute('aria-hidden','true')}});const topPanel=panels.get(topId);if(topPanel&&titleEl){const t=topPanel.getAttribute('data-panel-title');if(t)titleEl.textContent=t}
if(backBtn){if(stack.length>1)backBtn.classList.remove('is-hidden');else backBtn.classList.add('is-hidden')}
if(topPanel){const scroll=topPanel.querySelector('.autoid-drawer__scroll');if(scroll)scroll.scrollTop=0}
if(moveFocus&&topPanel){requestAnimationFrame(()=>focusFirstIn(topPanel))}}
function goTo(panelId){if(!panels.has(panelId))return;if(stack[stack.length-1]===panelId)return;stack.push(panelId);applyStack(!0)}
function goBack(){if(stack.length<=1)return;stack.pop();applyStack(!0)}
function reset(){stack=['root'];applyStack()}
function closeDrawer(){if(!details)return;const hd=details.closest('header-drawer');if(hd&&typeof hd.toggle==='function')hd.toggle();else details.removeAttribute('open')}
drawer.addEventListener('click',(e)=>{const goBtn=e.target.closest('[data-go-panel]');if(goBtn&&drawer.contains(goBtn)){e.preventDefault();const panelId=goBtn.getAttribute('data-go-panel');
if(panels.has(panelId)){goTo(panelId);return}
// A deep panel that has not arrived yet: wait for it, or follow the row's own link if it cannot be fetched.
goBtn.setAttribute('aria-busy','true');
loadDeepPanels(drawer).then(()=>{registerPanels();goTo(panelId)}).catch(()=>{const href=goBtn.dataset.fallbackHref;if(href)window.location.assign(href)}).finally(()=>goBtn.removeAttribute('aria-busy'));
return}
const back=e.target.closest('[data-back-panel]');if(back&&drawer.contains(back)){e.preventDefault();goBack();return}
const close=e.target.closest('[data-drawer-close]');if(close&&drawer.contains(close)){e.preventDefault();closeDrawer();return}
const letterBtn=e.target.closest('[data-az-letter]');if(letterBtn&&drawer.contains(letterBtn)&&!letterBtn.disabled){e.preventDefault();const L=letterBtn.getAttribute('data-az-letter');const rail=letterBtn.closest('[data-autoid-az-rail]');if(rail){rail.querySelectorAll('.autoid-drawer__az-letter.is-active').forEach((a)=>a.classList.remove('is-active'));letterBtn.classList.add('is-active')}
const scroll=letterBtn.closest('.autoid-drawer__panel')?.querySelector('[data-autoid-brand-scroll]');const first=scroll?.querySelector(`[data-letter="${L}"]`);if(scroll&&first){const top=first.offsetTop-scroll.offsetTop;scroll.scrollTo({top,behavior:'smooth'})}}});drawer.addEventListener('keydown',(e)=>{if(e.key!=='Escape')return;if(!details||!details.hasAttribute('open'))return;closeDrawer()});if(details){let wasOpen=details.hasAttribute('open');new MutationObserver(()=>{const isOpen=details.hasAttribute('open');if(isOpen)warmDeepPanels();if(wasOpen&&!isOpen){setTimeout(reset,50)}
wasOpen=isOpen}).observe(details,{attributes:!0,attributeFilter:['open']});
// Start on the first touch of the menu button, so the panels are usually in before the drawer finishes opening.
details.addEventListener('pointerdown',warmDeepPanels,{once:!0,passive:!0});details.addEventListener('focusin',warmDeepPanels,{once:!0})}
applyStack()}
initAutoidDrawer();
document.addEventListener('autoid:drawer:mount', (event) => {
  if (event.target instanceof Element) initAutoidDrawer(event.target);
});
